import { client } from '@api/rest/client';
import type {
  ArticleArchitectBccStatusDTO,
  ArticleArchitectUploadRecordDTO,
} from '@conversed/rest/src/contracts/article-architect/article-architect.dto';
import {
  type ArticleArchitectCompletedData,
  type ArticleArchitectErrorData,
  type ArticleArchitectProgressData,
  ArticleArchitectProgressPhase,
} from '@conversed/shared/src/central/dto/article-architect.dto';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import { toastErrorTranslated } from '@helpers/toast';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useArticleArchitectSocket } from './useArticleArchitectSocket';

export enum GenerateStatus {
  Idle = 'idle',
  Generating = 'generating',
  Completed = 'completed',
  Error = 'error',
}

export interface ProgressStep {
  labelKey: string;
  labelParams?: Record<string, unknown>;
  isDone: boolean;
  isError?: boolean;
  isWarning?: boolean;
}

const buildStepFromProgress = (
  data: ArticleArchitectProgressData,
  articleIndex?: number,
): ProgressStep => {
  switch (data.phase) {
    case ArticleArchitectProgressPhase.Reading:
      return {
        labelKey: 'article-architect.progress.step.reading',
        labelParams: { filename: data.filename ?? '' },
        isDone: false,
      };
    case ArticleArchitectProgressPhase.Pages:
      return {
        labelKey: 'article-architect.progress.step.pages',
        labelParams: { count: data.pageCount ?? 0 },
        isDone: false,
      };
    case ArticleArchitectProgressPhase.Extracting:
      return {
        labelKey: 'article-architect.progress.step.extracting',
        isDone: false,
      };
    case ArticleArchitectProgressPhase.Words:
      return {
        labelKey: 'article-architect.progress.step.words',
        labelParams: { count: data.wordCount ?? 0 },
        isDone: false,
      };
    case ArticleArchitectProgressPhase.Embedding:
      return {
        labelKey: 'article-architect.progress.step.embedding',
        labelParams: { processed: data.processed ?? 0, total: data.total ?? 0 },
        isDone: false,
      };
    case ArticleArchitectProgressPhase.Generating:
      return {
        labelKey: 'article-architect.progress.step.generating',
        isDone: false,
      };
    case ArticleArchitectProgressPhase.ArticleDrafted:
      return {
        labelKey: 'article-architect.progress.step.article-drafted',
        labelParams: { index: articleIndex ?? '', title: data.title ?? '' },
        // Article is already saved when this event fires — show as done immediately
        isDone: true,
      };
    case ArticleArchitectProgressPhase.CheckingConflicts:
      return {
        labelKey: 'article-architect.progress.step.checking-conflicts',
        isDone: false,
      };
    case ArticleArchitectProgressPhase.ConflictDetected:
      return {
        labelKey: 'article-architect.progress.step.conflict-detected',
        labelParams: {
          title: data.title ?? '',
          conflictingTitle: data.conflictingTitle ?? '',
        },
        isDone: true,
        isWarning: true,
      };
    case ArticleArchitectProgressPhase.Conflicts:
      return {
        labelKey: 'article-architect.progress.step.conflicts',
        isDone: false,
      };
    case ArticleArchitectProgressPhase.FileFailed:
      return {
        labelKey: 'article-architect.progress.step.file-failed',
        labelParams: {
          filename: data.filename ?? '',
          reason: data.reason ?? '',
        },
        isDone: true,
        isError: true,
      };
    case ArticleArchitectProgressPhase.FileNoContent:
      return {
        labelKey: 'article-architect.progress.step.file-no-content',
        labelParams: { filename: data.filename ?? '' },
        isDone: true,
        isWarning: true,
      };
  }
};

export const useArticleArchitectUploads = () => {
  const { selectedCompany } = useCompanySelector();

  const [uploads, setUploads] = useState<ArticleArchitectUploadRecordDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [bccStatus, setBccStatus] =
    useState<ArticleArchitectBccStatusDTO | null>(null);
  // Start as true so the card shows a neutral placeholder on first paint instead of the inactive state
  const [isBccStatusLoading, setIsBccStatusLoading] = useState(true);

  const [generatingFiles, setGeneratingFiles] = useState<TFile[]>([]);
  const [generateStatus, setGenerateStatus] = useState<GenerateStatus>(
    GenerateStatus.Idle,
  );
  const [generateResult, setGenerateResult] = useState<{
    articlesGenerated: number;
    conflictsDetected: number;
  } | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);

  // Tracks the index of the current article within a thread for step labels
  const articlesDraftedRef = useRef(0);
  // Tracks how many files/threads have started so we can detect multi-file transitions
  const currentFileIndexRef = useRef(-1);
  // Guards completed/error handlers against stale socket events from a previous generation
  // session that arrives after the user has navigated away and back.
  const hasInitiatedGenerationRef = useRef(false);

  const isGenerating = generateStatus === GenerateStatus.Generating;

  // Keep refs to fetch functions so socket callbacks can call them without stale closures
  const fetchUploadsRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const fetchBccStatusRef = useRef<(() => Promise<void>) | undefined>(
    undefined,
  );

  const fetchUploads = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await client.articleArchitect.getUploads({});
      if (response.data) {
        setUploads(response.data);
      }
    } catch (fetchError) {
      console.error('[ArticleArchitect] Failed to fetch uploads:', fetchError);
      toastErrorTranslated('article-architect.error.fetch-uploads-failed');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBccStatus = useCallback(async () => {
    setIsBccStatusLoading(true);
    try {
      const response = await client.articleArchitect.getBccStatus({});
      if (response.data) {
        setBccStatus(response.data);
      }
    } catch (statusError) {
      console.error(
        '[ArticleArchitect] Failed to fetch BCC status:',
        statusError,
      );
      toastErrorTranslated('article-architect.error.fetch-bcc-status-failed');
    } finally {
      setIsBccStatusLoading(false);
    }
  }, []);

  const handleProgress = useCallback((data: ArticleArchitectProgressData) => {
    // Reading signals the start of a new file/thread — advance index and reset per-file counters
    if (data.phase === ArticleArchitectProgressPhase.Reading) {
      currentFileIndexRef.current += 1;
      articlesDraftedRef.current = 0;
    }

    if (data.phase === ArticleArchitectProgressPhase.ArticleDrafted) {
      articlesDraftedRef.current += 1;
    }

    const newStep = buildStepFromProgress(
      data,
      data.phase === ArticleArchitectProgressPhase.ArticleDrafted
        ? articlesDraftedRef.current
        : undefined,
    );

    if (data.phase === ArticleArchitectProgressPhase.ArticleDrafted) {
      // "Generating articles..." stays as the active spinner while more articles are being generated.
      // Each article-drafted step is already done — just append it without touching the previous step.
      setProgressSteps((previousSteps) => [...previousSteps, newStep]);
    } else if (
      data.phase === ArticleArchitectProgressPhase.Reading &&
      currentFileIndexRef.current > 0
    ) {
      // Transitioning to a new file — mark ALL previous steps done (including any active
      // "Generating articles..." spinner from the previous file) before appending the new one
      setProgressSteps((previousSteps) => [
        ...previousSteps.map((step) => ({ ...step, isDone: true })),
        newStep,
      ]);
    } else if (
      data.phase === ArticleArchitectProgressPhase.CheckingConflicts ||
      data.phase === ArticleArchitectProgressPhase.ConflictDetected
    ) {
      // ConflictDetected steps are already marked done; CheckingConflicts replaces the previous active step
      if (data.phase === ArticleArchitectProgressPhase.ConflictDetected) {
        setProgressSteps((previousSteps) => [...previousSteps, newStep]);
      } else {
        // Mark all previous steps done then append the checking-conflicts spinner
        setProgressSteps((previousSteps) => [
          ...previousSteps.map((step) => ({ ...step, isDone: true })),
          newStep,
        ]);
      }
    } else {
      setProgressSteps((previousSteps) => {
        // Mark the previously active (last) step as done, then append the new one
        const updated = previousSteps.map((step, stepIndex) =>
          stepIndex === previousSteps.length - 1
            ? { ...step, isDone: true }
            : step,
        );
        return [...updated, newStep];
      });
    }
  }, []);

  const handleCompleted = useCallback((data: ArticleArchitectCompletedData) => {
    // Ignore completed events from a previous generation session that arrive after
    // the user navigated away and back (component remounted with fresh Idle state).
    if (!hasInitiatedGenerationRef.current) {
      return;
    }
    setProgressSteps((previousSteps) => {
      const allDone = previousSteps.map((step) => ({ ...step, isDone: true }));
      const conflictsDetected = data.conflictsDetected ?? 0;
      const summaryStep: ProgressStep =
        conflictsDetected > 0
          ? {
              labelKey:
                'article-architect.progress.step.articles-ready-with-conflicts',
              labelParams: {
                articles: data.articlesGenerated,
                conflicts: conflictsDetected,
              },
              isDone: true,
            }
          : {
              labelKey: 'article-architect.progress.step.articles-generated',
              labelParams: { count: data.articlesGenerated },
              isDone: true,
            };
      return [...allDone, summaryStep];
    });
    setGenerateStatus(GenerateStatus.Completed);
    setGenerateResult({
      articlesGenerated: data.articlesGenerated,
      conflictsDetected: data.conflictsDetected ?? 0,
    });
    // Refresh uploads and BCC status so counts are up to date after generation
    fetchUploadsRef.current?.();
    fetchBccStatusRef.current?.();
  }, []);

  const handleError = useCallback((data: ArticleArchitectErrorData) => {
    if (!hasInitiatedGenerationRef.current) {
      return;
    }
    console.error(
      '[ArticleArchitect] Generation error:',
      data.errorCode,
      data.errorData,
    );
    // Mark all existing steps as done before showing the error
    setProgressSteps((previousSteps) =>
      previousSteps.map((step) => ({ ...step, isDone: true })),
    );
    setGenerateStatus(GenerateStatus.Error);
    setGenerateError(null);
    // Refresh BCC inbox count (emails are moved even on failure) and the
    // uploads table (document upload records show their updated Failed status).
    fetchBccStatusRef.current?.();
    fetchUploadsRef.current?.();
  }, []);

  const { startGeneration, startBccBatch, isConnected } =
    useArticleArchitectSocket(handleProgress, handleCompleted, handleError);

  const handleGenerate = useCallback(
    async (files: TFile[]): Promise<boolean> => {
      if (!selectedCompany) {
        console.error('[ArticleArchitect] No company selected');
        return false;
      }

      setGeneratingFiles(files);
      setGenerateStatus(GenerateStatus.Generating);
      setGenerateResult(null);
      setGenerateError(null);
      setProgressSteps([]);
      articlesDraftedRef.current = 0;
      currentFileIndexRef.current = -1;
      hasInitiatedGenerationRef.current = true;

      const emitted = startGeneration({
        companyId: selectedCompany.id,
        files,
      });

      if (!emitted) {
        // Socket was not connected — fall back to error state immediately
        setGenerateStatus(GenerateStatus.Error);
        setGenerateError('article-architect.error.generate-failed');
        toastErrorTranslated('article-architect.error.generate-failed');
        return false;
      }

      return true;
    },
    [selectedCompany, startGeneration],
  );

  const handleGenerateDone = useCallback(() => {
    setGenerateStatus(GenerateStatus.Idle);
    setGeneratingFiles([]);
    setGenerateResult(null);
    setGenerateError(null);
    setProgressSteps([]);
    articlesDraftedRef.current = 0;
    currentFileIndexRef.current = -1;
    hasInitiatedGenerationRef.current = false;
  }, []);

  const handleTriggerBccBatch = useCallback(() => {
    if (!selectedCompany) {
      console.error('[ArticleArchitect] No company selected');
      return;
    }

    setGeneratingFiles([]);
    setGenerateStatus(GenerateStatus.Generating);
    setGenerateResult(null);
    setGenerateError(null);
    setProgressSteps([]);
    articlesDraftedRef.current = 0;
    currentFileIndexRef.current = -1;
    hasInitiatedGenerationRef.current = true;

    const emitted = startBccBatch(selectedCompany.id);
    if (!emitted) {
      // Socket was not connected — fall back to error state immediately
      setGenerateStatus(GenerateStatus.Error);
      setGenerateError('article-architect.error.generate-failed');
      toastErrorTranslated('article-architect.error.generate-failed');
    }
  }, [selectedCompany, startBccBatch]);

  useEffect(() => {
    fetchUploadsRef.current = fetchUploads;
    fetchBccStatusRef.current = fetchBccStatus;
    fetchUploads();
    fetchBccStatus();
  }, [fetchUploads, fetchBccStatus]);

  return {
    uploads,
    isLoading,
    isGenerating,
    isConnected,
    handleGenerate,
    fetchUploads,
    generatingFiles,
    generateStatus,
    generateResult,
    generateError,
    progressSteps,
    handleGenerateDone,
    bccStatus,
    isBccStatusLoading,
    handleTriggerBccBatch,
    fetchBccStatus,
  };
};
